import { useMemo, useState, useCallback } from 'react'
import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useAppStore } from '@/stores'
import { ConfirmModal } from '@/components/shared'
import { useTranslation } from 'react-i18next'
import { useNodeSaleStatus } from '@/hooks/useNodeSaleStatus'

export default function Tabbar() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const navigationGuard = useAppStore((s) => s.navigationGuard)
  const [pendingPath, setPendingPath] = useState<string | null>(null)
  const nodeOnSale = useNodeSaleStatus()

  // 节点与私募是当前的核心业务，各占一个一级入口。
  // 五个 tab 已是移动端底栏的舒适上限，再多会挤到点不准。
  // 曾有过「交易」页（嵌 ENI swap 组件），因 MCAI 实际在 BSC 而非 ENI 链已整页移除。
  //
  // 第二个 tab 跟着节点页的实际内容走：三档全部下架后 /node 整页是「短剧出海」预告，
  // tab 也改叫「短剧」。只有明确探测到「无在售档位」才切换，未知（null）时按「节点」兜底。
  const tabs = useMemo(() => [
    { path: '/home', label: t('tabbar.home'), icon: 'home' },
    nodeOnSale === false
      ? { path: '/node', label: t('tabbar.drama'), icon: 'movie' }
      : { path: '/node', label: t('tabbar.node'), icon: 'hub' },
    { path: '/presale', label: t('tabbar.presale'), icon: 'toll' },
    { path: '/stake', label: t('tabbar.stake'), icon: 'savings' },
    { path: '/me', label: t('tabbar.me'), icon: 'person' },
  ], [t, nodeOnSale])

  const handleClick = useCallback((e: React.MouseEvent, path: string) => {
    if (navigationGuard.active && path !== location.pathname) {
      e.preventDefault()
      setPendingPath(path)
    }
  }, [navigationGuard.active, location.pathname])

  const handleConfirmLeave = useCallback(() => {
    if (pendingPath) {
      navigate(pendingPath)
      setPendingPath(null)
    }
  }, [pendingPath, navigate])

  return (
    <>
      <nav
        className="fixed bottom-0 z-50 w-full flex items-stretch"
        style={{
          maxWidth: 430,
          height: 64,
          background: 'rgba(7,6,11,0.97)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderTop: '1px solid rgba(46,107,255,0.15)',
          boxShadow: '0 -1px 8px rgba(46,107,255,0.1)',
        }}
      >
        {tabs.map((tab) => (
          <NavLink
            key={tab.path}
            to={tab.path}
            onClick={(e) => handleClick(e, tab.path)}
            className="flex-1 flex flex-col items-center justify-center gap-0.5 relative no-underline transition-all duration-200"
            style={{ textDecoration: 'none' }}
          >
            {({ isActive }) => (
              <>
                <span
                  className="material-icons"
                  style={{ fontSize: 22, color: isActive ? '#5B8CFF' : '#52525B' }}
                >
                  {tab.icon}
                </span>
                <span
                  className="text-[10px] font-medium"
                  style={{ color: isActive ? '#5B8CFF' : '#52525B', }}
                >
                  {tab.label}
                </span>
                {isActive && (
                  <div
                    className="absolute bottom-0 rounded-t-full"
                    style={{ width: 32, height: 2, background: '#2E6BFF' }}
                  />
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <ConfirmModal
        open={pendingPath !== null}
        onClose={() => setPendingPath(null)}
        title={navigationGuard.title}
        message={navigationGuard.message}
        onConfirm={handleConfirmLeave}
      />
    </>
  )
}
